/**
 * Cross-window registry of active project runs needing desktop execution /
 * overlay presentation. Holds pointers only ("run X of project Y") — never
 * status. BE is source of truth; stale pointers are validated on read.
 *
 * Why this exists:
 *   The computer-use delegate machinery was originally tied to the lifetime
 *   of ``<ProjectChat />``. Navigating away while a ``run_computer_use_task``
 *   was in flight unmounted the chat, tearing down the run-stream
 *   subscription, the docked overlay card, and the claimed-tool-call state.
 *   The fix: hoist "which runs are active?" out of React into this
 *   module-level registry. A single app-shell ``<ProjectsBackgroundRunner />``
 *   subscribes to it and mounts a hidden execution/presentation session per
 *   entry, regardless of route. ``ProjectChat`` registers on mount;
 *   deregistration is driven by terminal run status (failed / closed), not by
 *   unmount — so navigating away keeps the run alive.
 *
 * Why it is persisted (localStorage), unlike the original in-memory version:
 *   1. an app restart mid-run re-mounts the background session, so the run's
 *      docked card survives a restart (the run itself lives on the BE), and
 *   2. the Cmd+Shift+H overlay webview registers its runs here (AGE-929),
 *      so the pointer must be readable across windows.
 *   Entries carry no status and expire after 24h; a corrupt store parses to
 *   empty. The Tauri poke exists because ``storage`` events don't fire
 *   reliably across Tauri webviews.
 */

import { logger, sanitizeError } from '@heidi/runtime/logger';
type UnlistenFn = () => void;

export interface ProjectBackgroundEntry {
  projectId: string;
  projectTitle: string;
  /**
   * Parent run id (registry key). One observer per backend run.
   */
  agentRunId: string;
  /** Which webview created the run — the overlay registers `'overlay'` (AGE-929). */
  origin: 'overlay' | 'main';
  /** Focus destination passed to the native executor presentation. */
  returnFocusTarget?: 'main' | 'overlay';
  /**
   * The run binds redactions (a note-push run). Non-PHI marker so a restart
   * restore can fail closed when the (memory-only) patient bind is gone,
   * instead of attaching the executor unredacted.
   */
  requiresRedactions?: boolean;
  /** Backend-frozen target mode. Missing persisted values are legacy false. */
  desktopTargetEnabled?: boolean;
  updatedAt: number;
}

export const PROJECTS_BACKGROUND_REGISTRY_KEY = 'projects-background-registry';
/** Same-window change notification (the `storage` event only fires cross-window). */
export const PROJECTS_BACKGROUND_REGISTRY_CHANGED_EVENT =
  'projects-background-registry-changed';
/** Entries older than this are dropped on read (BE validation is the real cleaner). */
export const PROJECTS_BACKGROUND_ENTRY_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** Stable server-snapshot value for `useSyncExternalStore` consumers. */
export const EMPTY_PROJECT_BACKGROUND_ENTRIES: readonly ProjectBackgroundEntry[] =
  Object.freeze([]);

export interface ProjectBackgroundRegistryPorts {
  emitProjectAgentRegistryPoke: () => Promise<unknown>;
  listenProjectAgentRegistryPoke: (callback: () => void) => Promise<() => void>;
}
export function createProjectsBackgroundRegistry({
  emitProjectAgentRegistryPoke,
  listenProjectAgentRegistryPoke,
}: ProjectBackgroundRegistryPorts) {
  type Listener = () => void;

  let _entries = new Map<string, ProjectBackgroundEntry>();
  let _hydratedFromStorage = false;
  const _listeners = new Set<Listener>();
  // Frozen snapshot for useSyncExternalStore — reference changes only on mutation.
  let _snapshot: readonly ProjectBackgroundEntry[] = Object.freeze([]);
  let _stopCrossWindowSync: (() => void) | null = null;

  const isEntryFresh = (entry: ProjectBackgroundEntry, now = Date.now()): boolean =>
    now - entry.updatedAt <= PROJECTS_BACKGROUND_ENTRY_MAX_AGE_MS;

  const parseEntry = (value: unknown): ProjectBackgroundEntry | null => {
    if (!value || typeof value !== 'object') return null;
    const obj = value as Record<string, unknown>;
    if (
      typeof obj.projectId !== 'string' ||
      !obj.projectId ||
      typeof obj.agentRunId !== 'string' ||
      !obj.agentRunId
    ) {
      return null;
    }
    return {
      projectId: obj.projectId,
      projectTitle: typeof obj.projectTitle === 'string' ? obj.projectTitle : '',
      agentRunId: obj.agentRunId,
      origin: obj.origin === 'overlay' ? 'overlay' : 'main',
      ...(obj.returnFocusTarget === 'overlay' || obj.returnFocusTarget === 'main'
        ? { returnFocusTarget: obj.returnFocusTarget }
        : {}),
      ...(obj.requiresRedactions === true ? { requiresRedactions: true } : {}),
      ...(obj.desktopTargetEnabled === true ? { desktopTargetEnabled: true } : {}),
      updatedAt:
        typeof obj.updatedAt === 'number' && Number.isFinite(obj.updatedAt)
          ? obj.updatedAt
          : Date.now(),
    };
  };

  const readStoredEntries = (): Map<string, ProjectBackgroundEntry> => {
    const entries = new Map<string, ProjectBackgroundEntry>();
    if (typeof window === 'undefined') return entries;
    try {
      const raw = window.localStorage.getItem(PROJECTS_BACKGROUND_REGISTRY_KEY);
      if (!raw) return entries;
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return entries;
      for (const value of parsed) {
        const entry = parseEntry(value);
        if (entry && isEntryFresh(entry)) entries.set(entry.agentRunId, entry);
      }
    } catch {
      // Corrupt storage → empty registry; BE remains source of truth.
    }
    return entries;
  };

  const persistEntries = (): void => {
    if (typeof window === 'undefined') return;
    try {
      if (_entries.size === 0) {
        window.localStorage.removeItem(PROJECTS_BACKGROUND_REGISTRY_KEY);
      } else {
        window.localStorage.setItem(
          PROJECTS_BACKGROUND_REGISTRY_KEY,
          JSON.stringify([..._entries.values()])
        );
      }
    } catch (error) {
      // A fixed message and the sanitised error: never the storage error's text.
      logger.debug('[projects-background-registry] Failed to persist registry', {
        error: sanitizeError(error),
      });
    }
  };

  const recomputeSnapshot = (): void => {
    _snapshot = Object.freeze(
      [..._entries.values()].map((entry) => Object.freeze({ ...entry }))
    );
  };

  const emit = (): void => {
    for (const listener of _listeners) {
      try {
        listener();
      } catch {
        // Independent listeners — one throw must not starve others.
      }
    }
  };

  /**
   * Meaningful-field equality — single source for register + `entriesEqual`.
   * `Record<MeaningfulKey, true>` makes the key list compile-time complete:
   * adding a field to the entry type without listing it here (or excluding it
   * like `updatedAt`) fails to compile — an omitted field would silently turn
   * its updates into register no-ops (this once dropped the durable
   * `requiresRedactions` marker).
   */
  type MeaningfulKey = keyof Omit<ProjectBackgroundEntry, 'updatedAt'>;
  const MEANINGFUL_KEY_FLAGS: Record<MeaningfulKey, true> = {
    projectId: true,
    projectTitle: true,
    agentRunId: true,
    origin: true,
    returnFocusTarget: true,
    requiresRedactions: true,
    desktopTargetEnabled: true,
  };
  const MEANINGFUL_KEYS = Object.keys(MEANINGFUL_KEY_FLAGS) as MeaningfulKey[];

  const entryFieldsEqual = (
    a: ProjectBackgroundEntry,
    b: ProjectBackgroundEntry
  ): boolean => MEANINGFUL_KEYS.every((key) => a[key] === b[key]);

  const entriesEqual = (
    a: Map<string, ProjectBackgroundEntry>,
    b: Map<string, ProjectBackgroundEntry>
  ): boolean => {
    if (a.size !== b.size) return false;
    for (const [key, entryA] of a) {
      const entryB = b.get(key);
      if (!entryB) return false;
      if (!entryFieldsEqual(entryA, entryB) || entryA.updatedAt !== entryB.updatedAt) {
        return false;
      }
    }
    return true;
  };

  const hydrateFromStorageIfNeeded = (): void => {
    if (_hydratedFromStorage) return;
    _hydratedFromStorage = true;
    _entries = readStoredEntries();
    recomputeSnapshot();
  };

  /** Re-read persisted state (another window may have written it) and notify. */
  const syncFromStorage = (): void => {
    hydrateFromStorageIfNeeded();
    const stored = readStoredEntries();
    if (entriesEqual(_entries, stored)) return;
    _entries = stored;
    recomputeSnapshot();
    emit();
  };

  const notifyOtherWindows = (): void => {
    if (typeof window === 'undefined') return;
    try {
      window.dispatchEvent(new Event(PROJECTS_BACKGROUND_REGISTRY_CHANGED_EVENT));
    } catch {
      // Best-effort same-window notification.
    }
    void emitProjectAgentRegistryPoke();
  };

  const startCrossWindowSync = (): (() => void) => {
    if (typeof window === 'undefined') return () => undefined;

    const onStorage = (event: StorageEvent) => {
      if (event.key !== PROJECTS_BACKGROUND_REGISTRY_KEY) return;
      syncFromStorage();
    };
    const onLocalChange = () => syncFromStorage();

    window.addEventListener('storage', onStorage);
    window.addEventListener(PROJECTS_BACKGROUND_REGISTRY_CHANGED_EVENT, onLocalChange);

    let cancelled = false;
    let unlistenPoke: UnlistenFn | null = null;
    void listenProjectAgentRegistryPoke(syncFromStorage).then((unlisten) => {
      if (cancelled) void unlisten();
      else unlistenPoke = unlisten;
    });

    return () => {
      cancelled = true;
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(
        PROJECTS_BACKGROUND_REGISTRY_CHANGED_EVENT,
        onLocalChange
      );
      void unlistenPoke?.();
    };
  };

  const applyMutation = (mutate: () => boolean): void => {
    hydrateFromStorageIfNeeded();
    // Refresh from storage first so a concurrent write from another window is
    // merged instead of clobbered by our whole-registry persist below.
    const stored = readStoredEntries();
    const externallyChanged = !entriesEqual(_entries, stored);
    if (externallyChanged) _entries = stored;
    const changed = mutate();
    if (!changed && !externallyChanged) return;
    if (changed) {
      persistEntries();
      notifyOtherWindows();
    }
    recomputeSnapshot();
    emit();
  };

  /**
   * Register (or update) a project's background presence. Safe to call
   * with identical args repeatedly — only mutates + persists when the
   * stored entry actually changes, so it won't loop ``useEffect`` chains.
   *
   * `updatedAt` is set on write and read only by the 24h freshness cap —
   * entries are NOT ordered by it (an executing run announces itself via the
   * executor snapshot, never via registry recency — see the AGE-930 pin).
   */
  const registerProjectBackground = (
    entry: Omit<ProjectBackgroundEntry, 'updatedAt'>
  ): void => {
    applyMutation(() => {
      const existing = _entries.get(entry.agentRunId);
      const merged = {
        ...entry,
        projectTitle: entry.projectTitle || existing?.projectTitle || '',
        returnFocusTarget: entry.returnFocusTarget ?? existing?.returnFocusTarget,
        // Latched here, the ONLY place: once a run is known to bind redactions
        // it stays marked (even against an explicit `false`) — unmarking would
        // reopen the fail-closed restore gap. Cleared only by entry removal.
        requiresRedactions:
          entry.requiresRedactions || existing?.requiresRedactions || undefined,
        // Frozen mode is authoritative when supplied by ProjectDetail. Preserve
        // it across writers that only know the run pointer.
        desktopTargetEnabled:
          entry.desktopTargetEnabled ?? existing?.desktopTargetEnabled ?? undefined,
      };
      if (existing && entryFieldsEqual(existing, { ...merged, updatedAt: 0 })) {
        return false;
      }
      _entries.set(entry.agentRunId, Object.freeze({ ...merged, updatedAt: Date.now() }));
      return true;
    });
  };

  const unregisterProjectBackground = (agentRunId: string): void => {
    applyMutation(() => _entries.delete(agentRunId));
  };

  const subscribeProjectBackground = (listener: Listener): (() => void) => {
    hydrateFromStorageIfNeeded();
    if (_listeners.size === 0 && !_stopCrossWindowSync) {
      _stopCrossWindowSync = startCrossWindowSync();
    }
    _listeners.add(listener);
    return () => {
      _listeners.delete(listener);
      if (_listeners.size === 0) {
        _stopCrossWindowSync?.();
        _stopCrossWindowSync = null;
      }
    };
  };

  const getProjectBackgroundSnapshot = (): readonly ProjectBackgroundEntry[] => {
    hydrateFromStorageIfNeeded();
    return _snapshot;
  };

  /**
   * Test-only helper. Clears every entry + listener so each test starts
   * from a known clean state. The registry is a module-level singleton
   * (intentionally — it has to outlive React mounts to keep background
   * runs alive across page navigation), which would otherwise leak
   * state between Jest test files / cases.
   *
   * NOT exported via any production code path; tests reach in directly
   * via the relative import.
   */
  const __resetProjectsBackgroundRegistryForTests = (): void => {
    _entries.clear();
    _listeners.clear();
    _snapshot = Object.freeze([]);
    _hydratedFromStorage = false;
    _stopCrossWindowSync?.();
    _stopCrossWindowSync = null;
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(PROJECTS_BACKGROUND_REGISTRY_KEY);
      } catch {
        // best-effort test cleanup
      }
    }
  };

  return {
    registerProjectBackground,
    unregisterProjectBackground,
    subscribeProjectBackground,
    getProjectBackgroundSnapshot,
    __resetProjectsBackgroundRegistryForTests,
  };
}
